import { Card, CardMedia } from '@material-ui/core';
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined';
import { eazyworksKlantenQueryParamsLsKey } from 'app/localStorageKeys';
import { NoRecordsFound } from 'framework/components/NoRecordsFound';
import { PageBreadcrumb } from 'framework/components/breadcrumbs/PageBreadcrumb';
import { SmallPrimaryTextButton } from 'framework/components/buttons/SmallPrimaryTextButton';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { IQueryContext } from 'framework/context/IQueryContext';
import { QueryProvider as FrameworkQueryProvider } from 'framework/context/QueryProvider';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { IHeader } from 'framework/table/IHeader';
import { PageableTableWithColumnSelection } from 'framework/table/PageableTableWithColumnSelection';
import { createEmptyHeader } from 'framework/table/createEmptyHeader';
import { createHeaderWithKey } from 'framework/table/createHeaderWithKey';
import { createSortableHeaderWithKey } from 'framework/table/createSortableHeaderWithKey';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { formatDate } from 'framework/utils/date/formatDate';
import {
	IEazyworksKlant,
	IEazyworksKlantenQueryParams,
	IQueryResult,
	filterSortPageEazyworksKlanten_concurrencyToken,
	filterSortPageEazyworksKlanten_handle,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { EazyworksPatientsRoute } from 'routes';
import { DetailsNavigateButton } from 'shared/DetailsNavigateButton';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';

export const EazyworkPatientDetailsRoute = `${EazyworksPatientsRoute}/:id`;
export const EazyworkPatientImportRoute = `${EazyworksPatientsRoute}/import/:id`;
export type EazyworkKlantSelectableColumn = 'id' | 'firstName' | 'lastName' | 'socialSecurityNumber' | 'dateOfBirth' | 'address' | 'dateCreated';
export const calcEazyworksKlantDetailRoute = (id: number) => EazyworkPatientDetailsRoute.replace(':id', id.toString());

const createColumnRecord = (strings: IStrings): Record<EazyworkKlantSelectableColumn, string> => {
	return {
		id: strings.id,
		firstName: strings.firstName,
		lastName: strings.lastName,
		socialSecurityNumber: strings.socialSecurityNumber,
		dateOfBirth: strings.dateOfBirth,
		address: strings.address,
		dateCreated: strings.dateCreated,
	};
};

const createHeaders = (colRecord: Record<EazyworkKlantSelectableColumn, string>): Record<EazyworkKlantSelectableColumn, IHeader<IEazyworksKlant>> => {
	return {
		dateCreated: createSortableHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(colRecord, 'dateCreated', s => formatDate(s.dateCreated)),
		id: createSortableHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(colRecord, 'id', s => s.id.toString()),
		firstName: createSortableHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(colRecord, 'firstName', s => s.firstName),
		lastName: createSortableHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(colRecord, 'lastName', s => s.lastName),
		socialSecurityNumber: createSortableHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(
			colRecord,
			'socialSecurityNumber',
			s => s.socialSecurityNumber
		),
		address: createHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(colRecord, 'address', s => s.formattedAddress),
		dateOfBirth: createSortableHeaderWithKey<IEazyworksKlant, EazyworkKlantSelectableColumn>(colRecord, 'dateOfBirth', s => formatDate(s.dateOfBirth)),
	};
};

export const EazyworksKlantenQueryContext = React.createContext<IQueryContext<IQueryResult<IEazyworksKlant>, IEazyworksKlantenQueryParams>>(undefined as any);

const DefaultParams: IEazyworksKlantenQueryParams = {
	searchString: '',
	sortParams: { direction: 'desc', property: 'id' },
	pageParams: { index: 1, size: 10 },
};

export const EazyworksKlantenQueryProvider = ({ children }: { children: any }) => {
	return (
		<FrameworkQueryProvider<IQueryResult<IEazyworksKlant>, IEazyworksKlantenQueryParams>
			defaultParams={DefaultParams}
			queryF={filterSortPageEazyworksKlanten_handle}
			concurrencyF={filterSortPageEazyworksKlanten_concurrencyToken}
			localStorageKey={eazyworksKlantenQueryParamsLsKey}
			context={EazyworksKlantenQueryContext}>
			{children}
		</FrameworkQueryProvider>
	);
};

export const EazyworksKlantenListPage = () => {
	const { push } = useHistory();

	const { params, setParams, queryResult, reload } = useQueryContext(EazyworksKlantenQueryContext);
	const strings = useLocalization();
	const columnRecord = useMemo(() => createColumnRecord(strings), [strings]);
	const headers = useMemo(() => createHeaders(columnRecord), [columnRecord]);
	const prependColumn = createEmptyHeader<IEazyworksKlant>(
		'empty',
		t => (
			<div className='df-row-ac gap-4'>
				<div className='fg1'></div>
				<SmallPrimaryTextButton
					onClick={() => push(EazyworkPatientImportRoute.replace(':id', t.id.toString()))}
					// disabled={disabled || route === undefined}
					// onClick={route === undefined ? undefined : () => push(route)}
				>
					{strings.import}
				</SmallPrimaryTextButton>
				<DetailsNavigateButton route={calcEazyworksKlantDetailRoute(t.id)} />
			</div>
		),
		'right'
	);
	// const { open, confirm, cancel } = useDialogsContext(reload);
	// const { push } = useHistory();

	if (queryResult === undefined) {
		return <div></div>;
	}

	// const onEid = () => {
	//     open(
	//         <EidForm
	//             cancel={cancel}
	//             confirm={id => {
	//                 confirm();
	//                 push(calcPatientDetailRoute(id));
	//             }}
	//         />
	//     );
	// };

	// const onNew = () => {
	//     open(
	//         <PatientForm
	//             cancel={cancel}
	//             confirm={id => {
	//                 confirm();
	//                 push(calcPatientDetailRoute(id));
	//             }}
	//         />
	//     );
	// };

	// const onBatchCreateFromNhax = () => {
	//     open(
	//         <BatchCreatePatientsFromNhaxFile
	//             cancel={cancel}
	//             confirm={confirm}
	//         />
	//     );
	// };

	return (
		<RestrictedPageX
			pageClaim='Patients'
			stickyHeader={
				<div
					className='df-row-ac'
					style={{ paddingBottom: 16 }}>
					<PageBreadcrumb
						text={`Eazyworks ${strings.patients}`}
						route={EazyworksPatientsRoute}
						icon={<GroupOutlinedIcon />}
					/>
					<div className='fg1'></div>
					<PageXHeaderActions>
						<PageReloadButtonX reload={reload} />
					</PageXHeaderActions>
				</div>
			}>
			<div
				className='df-col gap-16'
				style={{ padding: 16 }}>
				<SearchbarX
					placeholder={strings.searchOnLastName}
					value={params.searchString}
					onSearch={val => setParams(onResetToPage1({ ...params, searchString: val }))}
				/>
				{queryResult.unfilteredCount > 0 && (
					<Card>
						{/* <Divider /> */}
						{/* <div className='df-row jc-sb'>
							<PatientsListFilter
								params={params}
								onFilter={params => setParams(onResetToPage1(params))}
								paddingLeft={theme.spacing(1.5)}
							/>
						</div> */}
						{/* <Divider /> */}
						<CardMedia>
							<PageableTableWithColumnSelection<IEazyworksKlant, EazyworkKlantSelectableColumn>
								queryResult={queryResult}
								headers={headers}
								sortParams={params.sortParams}
								pageParams={params.pageParams}
								setParams={(sp, pp) => setParams({ ...params, sortParams: sp, pageParams: pp })}
								getKey={inst => inst.id.toString()}
								selectedColumns={['id', 'firstName', 'lastName', 'socialSecurityNumber', 'dateOfBirth', 'address', 'dateCreated']}
								appendColumn={prependColumn}
							/>
						</CardMedia>
					</Card>
				)}
				{queryResult.unfilteredCount === 0 && <NoRecordsFound>{strings.noPatientsDefined}</NoRecordsFound>}
			</div>
		</RestrictedPageX>
	);
};
